import React, {useMemo, useState, useRef, useCallback} from 'react';
import Select from 'react-select';

export default function WalletSelect({
                                         placeholder,
                                         options,
                                         value,
                                         onChange,

                                         className,
                                         classNamePrefix = 'wallet-select',
                                         styles: stylesOverride,
                                         formatOptionLabel,

                                         menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
                                         menuPosition = "fixed",
                                         menuShouldScrollIntoView = false,
                                         viewportPadding = 12,

                                         isSearchable = false,
                                         isClearable = true,
                                         isDisabled = false,
                                         onMenuOpen
                                     }) {
    const selectRef = useRef(null);
    const [menuPlacement, setMenuPlacement] = useState("bottom");
    const [maxMenuHeight, setMaxMenuHeight] = useState(280);

    const recomputeMenuFit = useCallback(() => {
        const controlEl = selectRef.current?.controlRef;
        if (!controlEl) return;

        const r = controlEl.getBoundingClientRect();

        const spaceBelow = window.innerHeight - r.bottom - viewportPadding;
        const spaceAbove = r.top - viewportPadding;

        const shouldOpenTop = spaceAbove > spaceBelow;

        setMenuPlacement(shouldOpenTop ? "top" : "bottom");

        const available = Math.max(120, shouldOpenTop ? spaceAbove : spaceBelow);
        setMaxMenuHeight(Math.min(360, available));
    }, [viewportPadding]);

    const styles = useMemo(() => {
        const base = {
            container: (provided) => ({
                ...provided,
                width: '100%',
                minHeight: 34
            }),

            control: (provided, state) => ({
                ...provided,
                background: 'var(--surface2)',
                border: `1px solid var(--border)`,
                borderRadius: 10,
                minHeight: 34,
                padding: '2px 4px',
                boxShadow: 'none',
                cursor: 'pointer',

                outline: state.isFocused
                    ?
                    '2px solid color-mix(in srgb, var(--primary) 65%, transparent)'
                    :
                    'none',
                outlineOffset: 2,

                '&:hover': {
                    borderColor: 'var(--border)',
                },
            }),

            valueContainer: (provided) => ({
                ...provided,
                padding: '2px 6px',
            }),

            input: (provided) => ({
                ...provided,
                color: 'var(--text)',
            }),

            singleValue: (provided) => ({
                ...provided,
                color: 'var(--text)',
                fontSize: 13,
                paddingLeft: '2px',
            }),

            placeholder: (provided) => ({
                ...provided,
                color: 'var(--text-muted)',
                fontSize: 13,
            }),

            indicatorSeparator: (provided) => ({
                ...provided,
                backgroundColor: 'color-mix(in srgb, var(--border) 85%, transparent)',
            }),

            dropdownIndicator: (provided) => ({
                ...provided,
                color: 'var(--text-muted)',
                padding: 4,
                '&:hover': {color: 'var(--text)'},
            }),

            clearIndicator: (provided) => ({
                ...provided,
                color: 'var(--text-muted)',
                padding: 4,
                '&:hover': {color: 'var(--text)'},
            }),

            menuPortal: (provided) => ({
                ...provided,
                zIndex: 'calc(var(--z-modal) + 5)',
            }),

            menu: (provided) => ({
                ...provided,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: 'var(--shadow)',
                overflow: 'auto',
                marginTop: 6,
            }),

            menuList: (provided) => ({
                ...provided,
                padding: 6,
                maxHeight: '40vh',
            }),

            option: (provided, state) => ({
                ...provided,
                borderRadius: 10,
                padding: '10px 10px',
                fontSize: 13,
                color: 'var(--text)',
                background: state.isFocused
                    ? 'var(--primary-soft)'
                    : state.isSelected
                        ? 'color-mix(in srgb, var(--primary) 18%, transparent)'
                        : 'transparent',
                cursor: 'pointer',
                '&:active': {
                    background: 'color-mix(in srgb, var(--primary) 26%, transparent)',
                },
            }),
        };

        if (!stylesOverride) return base;

        return {...base, ...stylesOverride};
    }, [stylesOverride]);

    return (
        <Select
            options={options}
            value={value ?? null}
            placeholder={placeholder}
            onChange={onChange}

            ref={selectRef}
            className={className}
            classNamePrefix={classNamePrefix}
            formatOptionLabel={formatOptionLabel}
            styles={styles}

            menuPosition={menuPosition}
            menuPlacement={menuPlacement}
            menuShouldScrollIntoView={menuShouldScrollIntoView}
            menuPortalTarget={menuPortalTarget}
            maxMenuHeight={maxMenuHeight}
            minMenuHeight={60}
            onMenuOpen={() => {
                recomputeMenuFit();
                onMenuOpen?.();
            }}

            isDisabled={isDisabled}
            isSearchable={isSearchable}
            isClearable={isClearable}
        />
    );
}